import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Grid,
  MenuItem,
  TextField,
  Typography
} from "@mui/material";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ContainerWrapper from "../../components/ContainerWrapper/ContainerWrapper";
import ActionButton from "../../components/Button/ActionButton";
import ConfigurationSection from "../../components/payments/ConfigurationSection/ConfigurationSection";
import HelpButton from "../../components/payments/HelpContent/HelpButton";
import useCreatePaymentConfiguration from "../../hooks/useCreatePaymentConfiguration";
import useProfessionalAccountStatus from "../../hooks/useProfessionalAccountStatus";
import useTaxIdAutofill from "../../hooks/useTaxIdAutofill";
import {
  ConfigurationFormErrors,
  ConfigurationFormValues,
  validateConfigurationForm
} from "./validation";
import { COMPANY_TYPES, CompanyType } from "../../types/payments";
import {
  DEFAULT_COUNTRY_CODE,
  countryOptions,
  formatPhoneNumber
} from "../../utils/countries";
import {
  FISCAL_ID_LENGTH,
  IBAN_MAX_LENGTH,
  PAYMENTS_HELP_CONTENT
} from "../../constants";
import { formColumnSx, outlinedFieldSx } from "../../utils/fieldStyles";
import {
  centeredActionsSx,
  pageHeaderRowSx,
  pageTitleSx
} from "../../utils/pageStyles";
import { semanticColors } from "../../utils/colors";
import { configurationNextStepsPath } from "../../utils/paymentsRoutes";
import { typography } from "../../utils/typography";

const warningTextSx = {
  ...typography.body.m,
  color: semanticColors.text.secondary
};

/** The form's own footer keeps a gap between the two buttons. */
const formActionsSx = { ...centeredActionsSx, gap: 2 };

const emptyValues: ConfigurationFormValues = {
  firstName: "",
  lastName: "",
  // Deliberately empty: see the note on the company type field below.
  easypayCompanyType: "",
  iban: "",
  fiscalId: "",
  organizationName: "",
  address: "",
  postalCode: "",
  city: "",
  country: DEFAULT_COUNTRY_CODE,
  phoneCountry: DEFAULT_COUNTRY_CODE,
  phoneNumber: ""
};

export default function PaymentConfigurationForm() {
  const { t } = useTranslation();
  const history = useHistory();
  const { givenName, familyName, email } = useProfessionalAccountStatus();
  const { create, loading: creating } = useCreatePaymentConfiguration();
  const { lookup } = useTaxIdAutofill();

  const [values, setValues] = useState<ConfigurationFormValues>(() => ({
    ...emptyValues,
    firstName: givenName ?? "",
    lastName: familyName ?? ""
  }));
  const [touched, setTouched] = useState<
    Partial<Record<keyof ConfigurationFormValues, boolean>>
  >({});

  // The profile is fetched asynchronously, so opening this route directly can
  // render the form before the name is known. Seed it when it arrives, but
  // never overwrite what the professional has already typed.
  useEffect(() => {
    setValues(current => ({
      ...current,
      firstName: current.firstName || givenName || "",
      lastName: current.lastName || familyName || ""
    }));
  }, [givenName, familyName]);

  const [submitError, setSubmitError] = useState<string | null>(null);

  const errors: ConfigurationFormErrors = useMemo(
    () => validateConfigurationForm(values),
    [values]
  );
  const isValid = Object.keys(errors).length === 0;

  const setValue = useCallback(
    (field: keyof ConfigurationFormValues, value: string) =>
      setValues(current => ({ ...current, [field]: value })),
    []
  );

  const markTouched = useCallback(
    (field: keyof ConfigurationFormValues) =>
      setTouched(current => ({ ...current, [field]: true })),
    []
  );

  /** Shows an error only once the user has left the field. */
  const fieldError = (field: keyof ConfigurationFormValues) =>
    touched[field] && errors[field] ? t(errors[field] as any) : "";

  /**
   * Prefills the company details from the tax id. Silent on failure — the
   * lookup already resolves to null on a timeout, a miss or an error.
   */
  const onFiscalIdBlur = useCallback(async () => {
    markTouched("fiscalId");
    if (values.fiscalId.length !== FISCAL_ID_LENGTH) return;

    const record = await lookup(values.fiscalId);
    if (!record) return;

    setValues(current => ({
      ...current,
      organizationName: record.title ?? current.organizationName,
      address: record.place?.address ?? current.address,
      postalCode: record.place
        ? `${record.place.pc4}-${record.place.pc3}`
        : current.postalCode,
      city: record.place?.city ?? current.city,
      country: DEFAULT_COUNTRY_CODE,
      phoneCountry: DEFAULT_COUNTRY_CODE,
      phoneNumber: record.contacts?.phone ?? current.phoneNumber
    }));
  }, [values.fiscalId, lookup, markTouched]);

  const onSubmit = useCallback(async () => {
    setSubmitError(null);
    try {
      const configId = await create({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        fiscalId: values.fiscalId,
        address: values.address.trim(),
        postalCode: values.postalCode,
        city: values.city.trim(),
        organizationName: values.organizationName.trim(),
        phone: formatPhoneNumber(values.phoneCountry, values.phoneNumber),
        iban: values.iban,
        easypayCompanyType: values.easypayCompanyType as CompanyType,
        // Not a visible field — the professional's own email is sent as is,
        // like the mobile does with the first entry of personalInfo.emails.
        email: email ?? ""
      });
      history.push(configurationNextStepsPath(configId));
    } catch {
      setSubmitError(t("paymentConfigurationForm.submitError"));
    }
  }, [values, email, create, history, t]);

  const textFieldProps = (
    field: keyof ConfigurationFormValues,
    label: string,
    placeholder?: string
  ) => ({
    label,
    placeholder: placeholder ?? label,
    value: values[field],
    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
      setValue(field, event.target.value),
    onBlur: () => markTouched(field),
    error: Boolean(fieldError(field)),
    helperText: fieldError(field),
    fullWidth: true,
    sx: outlinedFieldSx
  });

  return (
    <ContainerWrapper>
      <Box sx={formColumnSx}>
        <Box sx={pageHeaderRowSx}>
          <Typography sx={pageTitleSx}>
            {t("paymentConfigurationForm.title")}
          </Typography>
          <HelpButton linkName={PAYMENTS_HELP_CONTENT.ADD_CONFIG} />
        </Box>

        <ConfigurationSection>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {/*
                No option is preselected on purpose. The mobile app selects the
                first one automatically, which lets the professional submit
                INDIVIDUAL_NAME_BUSINESS_MAN without noticing — and the company
                type decides the KYC document matrix and cannot be changed later.
              */}
              <TextField
                select
                {...textFieldProps(
                  "easypayCompanyType",
                  t("paymentConfigurationForm.companyType")
                )}
              >
                {COMPANY_TYPES.map(companyType => (
                  <MenuItem key={companyType} value={companyType}>
                    {t(`companyType.${companyType}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                {...textFieldProps(
                  "organizationName",
                  t("paymentConfigurationForm.organizationName"),
                  t("paymentConfigurationForm.organizationNamePlaceholder")
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={warningTextSx}>
                {t("paymentConfigurationForm.companyTypeWarning")}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                {...textFieldProps(
                  "firstName",
                  t("paymentConfigurationForm.firstName")
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                {...textFieldProps(
                  "lastName",
                  t("paymentConfigurationForm.lastName")
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                {...textFieldProps(
                  "fiscalId",
                  t("paymentConfigurationForm.fiscalId"),
                  t("paymentConfigurationForm.fiscalIdPlaceholder")
                )}
                onBlur={onFiscalIdBlur}
                inputProps={{
                  maxLength: FISCAL_ID_LENGTH,
                  inputMode: "numeric"
                }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                {...textFieldProps(
                  "iban",
                  t("paymentConfigurationForm.iban"),
                  t("paymentConfigurationForm.ibanPlaceholder")
                )}
                inputProps={{ maxLength: IBAN_MAX_LENGTH }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                select
                {...textFieldProps(
                  "phoneCountry",
                  t("paymentConfigurationForm.phoneCountry")
                )}
              >
                {countryOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                {...textFieldProps(
                  "phoneNumber",
                  t("paymentConfigurationForm.phone")
                )}
                inputProps={{ inputMode: "tel" }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                {...textFieldProps(
                  "address",
                  t("paymentConfigurationForm.address"),
                  t("paymentConfigurationForm.addressPlaceholder")
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                {...textFieldProps(
                  "postalCode",
                  t("paymentConfigurationForm.postalCode")
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                {...textFieldProps("city", t("paymentConfigurationForm.city"))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                {...textFieldProps(
                  "country",
                  t("paymentConfigurationForm.country")
                )}
              >
                {countryOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </ConfigurationSection>

        {submitError && <Alert severity="error">{submitError}</Alert>}

        <Box sx={formActionsSx}>
          <ActionButton
            label={t("paymentConfigurations.back")}
            onClick={() => history.goBack()}
            variant="secondary"
          />
          <ActionButton
            label={t("paymentConfigurationForm.submit")}
            onClick={onSubmit}
            loading={creating}
            disabled={!isValid || creating}
          />
        </Box>
      </Box>
    </ContainerWrapper>
  );
}
